import type { BoardShape, CellCoord, Premium } from '../types';
import { BOARD_SIZE, boardCells } from './board';

/**
 * Fewest cells between a randomly drawn start square and the board's edge, counted the way a king moves: `3` keeps
 * the start out of the outer three rings, the 5 × 5 block around the centre of an 11 × 11 board.
 */
export const START_EDGE_MARGIN = 3;

/** Fewest cells between a randomly drawn start square and the nearest hole, counted the way a king moves. */
export const START_HOLE_MARGIN = 3;

/** How near the start square no premium square may be, counted the way a king moves: its eight neighbours. */
export const START_CLEARANCE = 1;

const chebyshev = (a: CellCoord, b: CellCoord): number => Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col));

/** Whether `cell` is the start square or within {@link START_CLEARANCE} of it. */
export const nearStart = (cell: CellCoord, start: CellCoord): boolean => chebyshev(cell, start) <= START_CLEARANCE;

/**
 * Every cell a random start may land on: in play, at least {@link START_EDGE_MARGIN} from each edge and at least
 * {@link START_HOLE_MARGIN} from every hole. Row by row, so a draw from it is repeatable.
 */
export function startCandidates(shape: BoardShape, size = BOARD_SIZE): CellCoord[] {
  const cells = boardCells(shape, size);
  const holes = cells.filter((cell) => cell.hole);
  return cells
    .filter((cell) => !cell.hole)
    .filter((cell) => Math.min(cell.row, cell.col, size - 1 - cell.row, size - 1 - cell.col) >= START_EDGE_MARGIN)
    .filter((cell) => holes.every((hole) => chebyshev(cell, hole) >= START_HOLE_MARGIN))
    .map(({ row, col }) => ({ row, col }));
}

/** The classic layout's word premiums on a full 11 × 11 board: a 3W in each corner and a 2W two in from each. */
export const CLASSIC_PREMIUMS: readonly Premium[] = [
  { row: 0, col: 0, kind: '3W' },
  { row: 0, col: 10, kind: '3W' },
  { row: 2, col: 2, kind: '2W' },
  { row: 2, col: 8, kind: '2W' },
  { row: 8, col: 2, kind: '2W' },
  { row: 8, col: 8, kind: '2W' },
  { row: 10, col: 0, kind: '3W' },
  { row: 10, col: 10, kind: '3W' },
];

/** The classic premiums a shape keeps: those off its holes and clear of the start square. Row by row. */
export function classicPremiums(shape: BoardShape, start: CellCoord, size = BOARD_SIZE): Premium[] {
  const playable = new Set(
    boardCells(shape, size)
      .filter((cell) => !cell.hole)
      .map((cell) => `${cell.row},${cell.col}`),
  );
  return CLASSIC_PREMIUMS.filter((p) => playable.has(`${p.row},${p.col}`) && !nearStart(p, start)).map((p) => ({
    ...p,
  }));
}
